import React, { useEffect, useRef, useState } from "react";
import { FileText, Copy, Check } from "lucide-react";

import { TruncatedTitle } from "../common/Primitives";
import { OutlineButton } from "../ui/Buttons";


export function LyricsCard({ task, onUseForMusic }) {
  const [countdown, setCountdown] = useState(30);
  const [timerExpired, setTimerExpired] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const createdAtRef = useRef(task.created_at ? new Date(task.created_at).getTime() : Date.now());

  const isPending = task.status === 'pending';
  const isCompleted = task.status === 'completed';
  const results = task.results || [];

  useEffect(() => {
    if (!isPending) return;
    const startTime = createdAtRef.current;
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, 30 - elapsed);
      setCountdown(remaining);
      if (remaining === 0) setTimerExpired(true);
    }, 1000);
    return () => clearInterval(timer);
  }, [isPending]);

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${mins}:${s.toString().padStart(2, '0')}`;
  };

  function copyLyrics(text, index) {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  }

  const showSpinner = isPending && timerExpired;

  if (isPending) {
    return (
      <div className="lyrics-card" data-testid={`lyrics-task-${task.id}`}>
        <div className="lyrics-header">
          <h3 className="lyrics-title">{task.description?.slice(0, 50) || 'Generating lyrics...'}</h3>
          <div className="song-timer">{formatTime(countdown)}</div>
        </div>
        <div className="subtle flex items-center gap-2">
          {showSpinner && <span className="spinner" />}
          <span>Processing...</span>
        </div>
      </div>
    );
  }

  if (task.status === 'failed') {
    return (
      <div className="lyrics-card" data-testid={`lyrics-task-${task.id}`}>
        <div className="lyrics-header">
          <h3 className="lyrics-title">{task.description?.slice(0, 50)}</h3>
        </div>
        <div className="subtle text-red">Generation failed</div>
      </div>
    );
  }

  return (
    <>
      {results.map((lr, idx) => (
        <div className="lyrics-card" key={`${task.id}-${idx}`} data-testid={`lyrics-result-${task.id}-${idx}`}>
          <div className="lyrics-header">
            <h3 className="lyrics-title">{lr.title}</h3>
            <div className="lyrics-actions">
              <button
                className="btn-icon"
                onClick={() => copyLyrics(lr.lyrics, idx)}
                title="Copy lyrics"
              >
                {copiedIndex === idx ? <Check size={16} /> : <Copy size={16} />}
              </button>
              <OutlineButton onClick={() => onUseForMusic(lr.lyrics, lr.title)}>
                Use for Music
              </OutlineButton>
            </div>
          </div>
          <pre className="lyrics-content">{lr.lyrics}</pre>
        </div>
      ))}
    </>
  );
}


export function LyricsLibraryCard({ task, onClick, inAllTab }) {
  const [countdown, setCountdown] = useState(30);
  const [timerExpired, setTimerExpired] = useState(false);
  const createdAtRef = useRef(task.created_at ? new Date(task.created_at).getTime() : Date.now());

  const isPending = task.status === 'pending';
  const isCompleted = task.status === 'completed';
  const isFailed = task.status === 'failed';

  useEffect(() => {
    if (!isPending) return;
    const startTime = createdAtRef.current;
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, 30 - elapsed);
      setCountdown(remaining);
      if (remaining === 0) setTimerExpired(true);
    }, 1000);
    return () => clearInterval(timer);
  }, [isPending]);

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${mins}:${s.toString().padStart(2, '0')}`;
  };

  const displayTitle = isCompleted && task.title ? task.title : (task.description?.slice(0, 40) || 'Lyrics');

  return (
    <div
      className={`library-card library-card-lyrics ${isCompleted ? 'library-card-clickable' : ''}`}
      onClick={isCompleted ? () => onClick(task, task.resultIndex || 0) : undefined}
      data-testid={`lyrics-lib-${task.id}`}
    >
      <div className="library-card-icon">
        <FileText size={24} />
      </div>

      <div className="library-card-content">
        <TruncatedTitle title={displayTitle} />
        <div className="library-card-subtitle subtle">
          {inAllTab && <span className="type-label type-lyrics">Lyrics</span>}
          {isCompleted ? (
            <span>Click to view</span>
          ) : isFailed ? (
            <span className="text-red">Failed</span>
          ) : null}
        </div>
      </div>

      {isPending && (
        <div className="library-card-status">
          {timerExpired ? (
            <span className="spinner" />
          ) : (
            <div className="song-timer">{formatTime(countdown)}</div>
          )}
        </div>
      )}
    </div>
  );
}
